
import { useModeAwareQuery } from "./useModeAwareQuery";
import { getTransactions, type GetTransactionsParams } from "@/lib/api/transactions";
import { mapTransactionApiToUi } from "@/lib/mappers/transactions";
import { generateMockTransactions } from "@/lib/mock-transactions";
import type { Transaction } from "@/types/transaction";

/**
 * Hook to fetch transactions list data
 * Handles both Demo and Live modes
 */
export function useTransactionsList(params?: GetTransactionsParams) {
  const mockTransactions = generateMockTransactions();

  return useModeAwareQuery<Transaction[], Error>(
    ["transactions", params],
    async ({ signal }) => {
      const response = await getTransactions(params, signal);
      return response.data.map(mapTransactionApiToUi);
    },
    mockTransactions,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Combined hook for transactions page data
 * Returns transactions list with loading and error states
 */
export function useTransactionsPageData(params?: GetTransactionsParams) {
  const transactionsQuery = useTransactionsList(params);

  return {
    transactions: transactionsQuery.data ?? [],
    isLoading: transactionsQuery.isLoading,
    error: transactionsQuery.error,
    refetch: () => {
      transactionsQuery.refetch();
    },
  };
}

/**
 * Hook to fetch transactions for a specific customer
 * Filters transactions by customerId and outstanding balance
 */
export function useCustomerTransactions(customerId: string | undefined) {
  const transactionsQuery = useTransactionsList();
  const customerIdNum = customerId ? parseInt(customerId, 10) : undefined;

  // Filter transactions for the specific customer with outstanding balance > 0
  const customerTransactions = (transactionsQuery.data ?? []).filter(
    (transaction) => 
      transaction.customerId === customerIdNum &&
      transaction.outstandingAmount !== undefined &&
      transaction.outstandingAmount > 0
  );

  return {
    transactions: customerTransactions,
    isLoading: transactionsQuery.isLoading,
    error: transactionsQuery.error,
    refetch: () => {
      transactionsQuery.refetch();
    },
  };
}

/**
 * Hook to fetch completed transactions for a specific customer
 * Filters transactions by customerId where outstanding balance = 0
 */
export function useCustomerCompletedTransactions(customerId: string | undefined) {
  const transactionsQuery = useTransactionsList();
  const customerIdNum = customerId ? parseInt(customerId, 10) : undefined;

  // Filter transactions for the specific customer with outstanding balance = 0
  const completedTransactions = (transactionsQuery.data ?? []).filter(
    (transaction) => 
      transaction.customerId === customerIdNum &&
      (transaction.outstandingAmount === 0 || 
       (transaction.outstandingAmount === undefined && transaction.status === "completed"))
  );

  return {
    transactions: completedTransactions,
    isLoading: transactionsQuery.isLoading,
    error: transactionsQuery.error,
    refetch: () => {
      transactionsQuery.refetch();
    },
  };
}
